import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';

const FAVORITES_ACCENT = '#EC4899';

const createStyles = (colors, isXS) => StyleSheet.create({
  wrap: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: isXS ? 28 : 48,
    paddingHorizontal: isXS ? 8 : 16,
  },
  iconRing: {
    width: isXS ? 72 : 80,
    height: isXS ? 72 : 80,
    borderRadius: isXS ? 22 : 24,
    backgroundColor: `${FAVORITES_ACCENT}18`,
    borderWidth: 1,
    borderColor: `${FAVORITES_ACCENT}40`,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: {
    textAlign: 'center',
    fontSize: isXS ? 22 : 26,
    lineHeight: isXS ? 28 : 32,
    marginBottom: 10,
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 440,
    lineHeight: 22,
    marginBottom: 24,
  },
  hints: {
    width: '100%',
    maxWidth: 480,
    gap: 10,
    marginBottom: 24,
  },
  hintCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: isXS ? 12 : 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
  },
  hintIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${FAVORITES_ACCENT}1A`,
  },
  hintCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  actions: {
    flexDirection: isXS ? 'column' : 'row',
    alignItems: 'center',
    gap: 10,
  },
  actionBtn: {
    minWidth: isXS ? 220 : 200,
  },
});

/**
 * Empty state when Explore "My favorites" has nothing to show.
 *
 * @param {() => void} onBrowseExplore — clears the favorites filter and shows the full gallery
 */
function ExploreFavoritesEmptyState({ onBrowseExplore }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  return (
    <View style={styles.wrap}>
      <View style={styles.iconRing}>
        <Icon name="heart" size={isXS ? 30 : 34} color={FAVORITES_ACCENT} solid />
      </View>

      <Text weight="extraBold" style={styles.title}>
        {t('explore.favoritesEmptyTitle') || 'No favorites yet'}
      </Text>
      <Text size="n" color="secondary" style={styles.subtitle}>
        {t('explore.favoritesEmptySubtitle')
          || 'Save images, videos, music, and 3D pieces you love — tap the heart on any item in Explore.'}
      </Text>

      <View style={styles.hints}>
        <View style={styles.hintCard}>
          <View style={styles.hintIcon}>
            <Icon name="grid-2" size={16} color={FAVORITES_ACCENT} solid />
          </View>
          <View style={styles.hintCopy}>
            <Text size="s" weight="bold">
              {t('explore.favoritesEmptyGalleryHint') || 'From the gallery'}
            </Text>
            <Text size="s" color="secondary" style={{ lineHeight: 20 }}>
              {t('explore.favoritesEmptyGalleryHintDesc')
                || 'Browse Explore and tap the heart on any card to save it here.'}
            </Text>
          </View>
        </View>
        <View style={styles.hintCard}>
          <View style={styles.hintIcon}>
            <Icon name="expand" size={16} color={FAVORITES_ACCENT} solid />
          </View>
          <View style={styles.hintCopy}>
            <Text size="s" weight="bold">
              {t('explore.favoritesEmptyDetailHint') || 'From an open item'}
            </Text>
            <Text size="s" color="secondary" style={{ lineHeight: 20 }}>
              {t('explore.favoritesEmptyDetailHintDesc')
                || 'Open any piece to view details, then use the heart to add or remove it from favorites.'}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.actions}>
        <Button
          text={t('explore.favoritesEmptyBrowse') || 'Browse Explore'}
          variant="primary"
          prependIcon="grid-2"
          onPress={onBrowseExplore}
          style={styles.actionBtn}
        />
      </View>
    </View>
  );
}

export default React.memo(ExploreFavoritesEmptyState);
